import {
  FormSectionElements,
  FormSections,
  FormSubSections,
  ProgramContainer,
} from "../entities/formTemplateDataType";
import { getAddress } from "./addressUtils";
import { UPloadFileItemProps } from "../runtimeComponent/subComponent/FileUploadComponent";
import SessionStorageService from "../services/SessionStorageService";
import { getCxpAccessToken } from "../services/CxpProfileService";
import axios from "axios";

import * as CircularJSON from "circular-json";
import { apiUrl } from "../config";
import { getRootComponent } from "./ElementUtils";
import { SEPERATOR, TOKEN_TYPE } from "../entities/Constants";
import { parseJson, stringifyJson } from "./JsonUtils";
import { removeHtmlTags } from "./HtmlUtils";
import { getEnvVar } from "../config/RuntimeConfig";

import SectionUtils from "./SectionUtils";
import { DBService } from "../services/IndexedDbService";
import {
  generateBase64PdfTemplate,
  generateHtmlForPdf,
} from "../services/PDFGenerationService";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { getSelectedService } from "../commonComponent/TileSelectionComponent";
import { FileUpload } from "primereact/fileupload";

export type DraftMetadata = {
  id?: string;
  draftId?: string;
  userId?: string;
  formId?: string;
  saved?: boolean;
};

const DRAFT_METADATA_KEY_PREFIX = "draftMetadata_";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class DataUtils {
  private static buildDraftMetadataKey = (categoryId?: string | null) => {
    const normalized = categoryId?.trim();
    if (!normalized) {
      return null;
    }
    return `${DRAFT_METADATA_KEY_PREFIX}${normalized.toLowerCase()}`;
  };

  private static normalizeMetadataString = (
    value: unknown,
  ): string | undefined => {
    if (value == null) {
      return undefined;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return value.toString();
    }
    return undefined;
  };

  private static normalizeEmailString = (
    value: unknown,
  ): string | undefined => {
    const normalized = DataUtils.normalizeMetadataString(value);
    if (!normalized) {
      return undefined;
    }
    return EMAIL_REGEX.test(normalized) ? normalized : undefined;
  };

  public static resolveEmailCandidate = (
    ...candidates: unknown[]
  ): string | undefined => {
    for (const candidate of candidates) {
      const email = DataUtils.normalizeEmailString(candidate);
      if (email) {
        return email;
      }
    }
    return undefined;
  };

  private static sanitizeMetadataUserId = (
    metadata: DraftMetadata | null,
  ): DraftMetadata | null => {
    if (!metadata) {
      return metadata;
    }
    const email = DataUtils.resolveEmailCandidate(metadata.userId);
    if (email) {
      return { ...metadata, userId: email };
    }

    if (metadata.userId) {
      const { userId, ...rest } = metadata;
      return { ...rest };
    }

    return metadata;
  };

  private static parseMetadataCandidate = (value: string): unknown => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return trimmed;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  };

  private static extractDraftMetadata = (
    raw: unknown,
  ): DraftMetadata | null => {
    if (raw == null) {
      return null;
    }
    if (
      typeof raw === "string" ||
      typeof raw === "number" ||
      typeof raw === "boolean"
    ) {
      const normalized = DataUtils.normalizeMetadataString(raw);
      return normalized ? { id: normalized, draftId: normalized } : null;
    }
    if (typeof raw !== "object") {
      return null;
    }

    const lowerMap: Record<string, unknown> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      lowerMap[key.toLowerCase()] = value;
    });

    const metadata: DraftMetadata = {};

    const draftIdentifier =
      DataUtils.normalizeMetadataString(lowerMap["draftid"]) ??
      DataUtils.normalizeMetadataString(lowerMap["draft_id"]) ??
      DataUtils.normalizeMetadataString(lowerMap["id"]) ??
      DataUtils.normalizeMetadataString(lowerMap["itemid"]);
    if (draftIdentifier) {
      metadata.draftId = draftIdentifier;
      if (!metadata.id) {
        metadata.id = draftIdentifier;
      }
    }

    const explicitId = DataUtils.normalizeMetadataString(lowerMap["id"]);
    if (explicitId) {
      metadata.id = explicitId;
      if (!metadata.draftId) {
        metadata.draftId = explicitId;
      }
    }

    const formIdentifier =
      DataUtils.normalizeMetadataString(lowerMap["formid"]) ??
      DataUtils.normalizeMetadataString(lowerMap["form_id"]);
    if (formIdentifier) {
      metadata.formId = formIdentifier;
    }

    const userIdentifier = DataUtils.resolveEmailCandidate(
      lowerMap["userid"],
      lowerMap["user_id"],
      lowerMap["user"],
      lowerMap["email"],
      lowerMap["useremail"],
    );
    if (userIdentifier) {
      metadata.userId = userIdentifier;
    }

    return Object.keys(metadata).length ? metadata : null;
  };

  private static getDraftIdFromSubmitApiResponse = (): string | undefined => {
    try {
      const raw = sessionStorage.getItem("submitApiResponse");
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return DataUtils.normalizeMetadataString(parsed?.draftId);
    } catch {
      return undefined;
    }
  };

  private static selectPreferredDraftRecord = (drafts: unknown[]): unknown => {
    if (!Array.isArray(drafts) || !drafts.length) {
      return null;
    }

    const getTimestamp = (draft: unknown): number => {
      if (!draft || typeof draft !== "object") {
        return 0;
      }
      const record = draft as Record<string, unknown>;
      const candidates = [
        record.updatedAt,
        record.updated_at,
        record.modifiedAt,
        record.modified_at,
        record.createdAt,
        record.created_at,
      ];

      for (const candidate of candidates) {
        if (typeof candidate === "number" && Number.isFinite(candidate)) {
          return candidate;
        }
        if (typeof candidate === "string") {
          const parsed = Date.parse(candidate);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }

      return 0;
    };

    return [...drafts].sort(
      (left, right) => getTimestamp(right) - getTimestamp(left),
    )[0];
  };

  static findExistingDraftMetadata = async (
    categoryId: string,
    userId: string | undefined,
    token: string,
  ): Promise<DraftMetadata | null> => {
    const baseUrl = getEnvVar("LAYOUT_API_URL");
    if (!baseUrl || !categoryId || !token) {
      return null;
    }

    try {
      const response = await axios.get(`${baseUrl}/data/admin/drafts/id`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const records = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];

      const matchingDrafts = records.filter((record) => {
        const metadata = DataUtils.sanitizeMetadataUserId(
          DataUtils.extractDraftMetadata(record),
        );
        if (!metadata) {
          return false;
        }

        const sameForm =
          (metadata.formId || "").toLowerCase() === categoryId.toLowerCase();
        if (!sameForm) {
          return false;
        }

        const normalizedUserId = DataUtils.resolveEmailCandidate(userId);
        if (!normalizedUserId) {
          return true;
        }

        const normalizedDraftUserId = DataUtils.resolveEmailCandidate(
          metadata.userId,
        );

        // The endpoint is already bearer-token scoped to the current user.
        // Prefer an explicit same-user match when present, but do not reject
        // same-form drafts whose metadata omits or normalizes userId differently.
        return (
          !normalizedDraftUserId ||
          normalizedDraftUserId.toLowerCase() === normalizedUserId.toLowerCase()
        );
      });

      const preferredRecord = DataUtils.selectPreferredDraftRecord(matchingDrafts);
      return DataUtils.sanitizeMetadataUserId(
        DataUtils.extractDraftMetadata(preferredRecord),
      );
    } catch (error) {
      console.warn("Unable to resolve existing draft metadata", error);
      return null;
    }
  };

  static persistDraftMetadata = (
    categoryId?: string | null,
    metadata?: DraftMetadata | null,
  ): void => {
    const metadataKey = DataUtils.buildDraftMetadataKey(categoryId);
    if (!metadataKey || !metadata) {
      return;
    }

    const sanitized: DraftMetadata = {};
    if (metadata.id) {
      sanitized.id = metadata.id.toString();
    }
    if (metadata.draftId) {
      sanitized.draftId = metadata.draftId.toString();
    }
    if (metadata.formId) {
      sanitized.formId = metadata.formId.toString();
    }

    const email = DataUtils.resolveEmailCandidate(metadata.userId);
    if (email) {
      sanitized.userId = email;
    }

    if (metadata.saved) {
      sanitized.saved = true;
    }

    if (!Object.keys(sanitized).length) {
      return;
    }

    sessionStorage.setItem(metadataKey, JSON.stringify(sanitized));
  };

  static getDraftMetadataFromSession = (
    categoryId?: string | null,
  ): DraftMetadata | null => {
    const metadataKey = DataUtils.buildDraftMetadataKey(categoryId);
    if (metadataKey) {
      const cachedMetadata = sessionStorage.getItem(metadataKey);
      if (cachedMetadata) {
        try {
          const parsed = JSON.parse(cachedMetadata) as DraftMetadata;
          const sanitized = DataUtils.sanitizeMetadataUserId(parsed);
          if (sanitized && Object.keys(sanitized).length) {
            return sanitized;
          }
        } catch {
          sessionStorage.removeItem(metadataKey);
        }
      }
    }

    const normalized = categoryId?.trim();
    if (!normalized) {
      return null;
    }

    const candidateKeys = Array.from(
      new Set([
        `draftId_${normalized}`,
        `draftId_${normalized.toLowerCase()}`,
        `draftId_${normalized.toUpperCase()}`,
      ]),
    );

    for (const key of candidateKeys) {
      const rawValue = sessionStorage.getItem(key);
      if (!rawValue) {
        continue;
      }
      const parsedCandidate = DataUtils.parseMetadataCandidate(rawValue);
      const metadata = DataUtils.sanitizeMetadataUserId(
        DataUtils.extractDraftMetadata(parsedCandidate),
      );
      if (metadata) {
        if (!metadata.formId) {
          metadata.formId = normalized;
        }
        DataUtils.persistDraftMetadata(normalized, metadata);
        return metadata;
      }
    }

    return null;
  };

  private static findControlToBind = (programContainer, fieldName) => {
    let returnedEle;
    outerLoop: for (let formSection of programContainer.ListOfFormSections) {
      for (let subSection of formSection.ListOfFormSubSections) {
        for (let ele of subSection.ListOfFormSectionElements) {
          if (ele.Datatype === "Row") {
            if (ele.subSectionList != null) {
              for (let eleSub of ele.subSectionList) {
                for (let eleSubSub of eleSub.ListOfFormSectionElements) {
                  if (eleSubSub.LoadBindName == fieldName) {
                    returnedEle = eleSubSub;
                    break outerLoop;
                  }
                }
              }
            }
          } else if (ele.LoadBindName == fieldName) {
            returnedEle = ele;
            break outerLoop;
          }
        }
      }
    }
    return returnedEle;
  };

  private static findAllControlsToBind = (programContainer, fieldName) => {
    let elements = [];
    for (let formSection of programContainer.ListOfFormSections) {
      for (let subSection of formSection.ListOfFormSubSections) {
        for (let ele of subSection.ListOfFormSectionElements) {
          if (ele.Datatype === "Row") {
            if (ele.subSectionList != null) {
              for (let eleSub of ele.subSectionList) {
                for (let eleSubSub of eleSub.ListOfFormSectionElements) {
                  if (eleSubSub.LoadBindName == fieldName) {
                    elements.push(eleSubSub);
                  }
                }
              }
            }
          } else if (ele.LoadBindName == fieldName) {
            elements.push(ele);
          }
        }
      }
    }
    return elements;
  };

  private static findControlsWithAliases = (programContainer, fieldName) => {
    const elements = DataUtils.findAllControlsToBind(
      programContainer,
      fieldName,
    );
    if (elements.length > 0) {
      return elements;
    }

    const aliases: Record<string, string[]> = {
      middleInitial: ["middleName"],
      middleName: ["middleInitial"],
    };

    const aliasFields = aliases[fieldName] || [];
    for (const aliasField of aliasFields) {
      const aliasElements = DataUtils.findAllControlsToBind(
        programContainer,
        aliasField,
      );
      if (aliasElements.length > 0) {
        return aliasElements;
      }
    }

    return elements;
  };

  static assignFormValue = (programContainer: ProgramContainer) => {
    const entity = programContainer.Entity;
    const formValue = entity?.formValue;
    let formValueObject;
    if (typeof formValue === "string" && formValue) {
      formValueObject = CircularJSON.parse(formValue);
    } else if (formValue) {
      formValueObject = formValue;
    } else {
      formValueObject = entity;
    }

    const flattenedEntity = DataUtils.flattenObject(formValueObject);
    Object.entries(flattenedEntity).forEach(([key, value]) => {
      const normalizedValue = unsanitizeDeep(value);

      const elements = DataUtils.findControlsWithAliases(programContainer, key);
      elements.forEach((element) => {
        if (element != null) {
          element.Value = normalizedValue;
          if (
            element.Datatype == "Checkbox" &&
            "Y" !== programContainer.CheckboxValueYN
          ) {
            if (value === "Y" || value === "N") {
              element.Value = value;
            } else {
              element.Value = value ? "Y" : "N";
            }
          } else if (element.Datatype == "Address") {
            if (flattenedEntity[key + "_StringObject"]) {
              const addressPayload = unsanitizeString(
                flattenedEntity[key + "_StringObject"],
              );
              element.Address = JSON.parse(addressPayload);
              element.manualAddressChecked =
                element.Address.manualAddressChecked;
            }
          }
        }
      });

      if (key.endsWith("_additonal_comment")) {
        const fieldName = key.replace("_additonal_comment", "");
        const cmaElements = DataUtils.findAllControlsToBind(
          programContainer,
          fieldName,
        );
        cmaElements.forEach((cmaElement) => {
          if (cmaElement && cmaElement.Datatype === "CMAQuestion") {
            cmaElement.CMAQuestionComment = normalizedValue;
          }
        });
      }
    });
  };

  static restructureForDupSection = (programContainer: ProgramContainer) => {
    const formValueObject = programContainer.Entity;
    const flattenedEntity = DataUtils.flattenObject(formValueObject);
    const map = new Map<string, number[]>();
    Object.keys(flattenedEntity).forEach((key) => {
      if (key.includes(SEPERATOR)) {
        const section = SectionUtils.findSectionByFieldName(
          programContainer,
          key,
        );
        if (section && section.duplicateGroupID) {
          const groupIndex = key.split(SEPERATOR)[1];
          if (!map.has(section.duplicateGroupID)) {
            map.set(section.duplicateGroupID, []);
          }
          const arr = map.get(section.duplicateGroupID)!;
          if (!arr.includes(Number(groupIndex))) {
            arr.push(Number(groupIndex));
            arr.sort((a, b) => a - b);
          }
          //map.set(section.duplicateGroupID, arr);
        }
      }
    });

    DataUtils.resetDuplicateSections(programContainer, map);
  };

  static resetDuplicateSections = (
    programContainer: ProgramContainer,
    map: Map<string, number[]>,
  ) => {
    map.forEach((value, key) => {
      const sections = programContainer.ListOfFormSections;
      const firstGroupIndex = sections.findIndex(
        (s) => s.duplicateGroupID === key,
      );
      let current = null;
      if (firstGroupIndex >= 0) {
        current = sections[firstGroupIndex - 1];
      }

      programContainer.ListOfFormSections = sections.filter(
        (s) => s.duplicateGroupID !== key,
      );
      value.forEach((groupIndex) => {
        SectionUtils.duplicateSection(
          programContainer,
          key,
          current,
          groupIndex,
        );
        for (
          let i = programContainer.ListOfFormSections.length - 1;
          i >= 0;
          i--
        ) {
          const section = programContainer.ListOfFormSections[i];
          if (section.duplicateGroupID === key) {
            current = section;
            break; // stop at the first match from the end
          }
        }
      });
    });
  };

  static initDuplicateSections = (programContainer: ProgramContainer) => {
    const duplicatableGroupMap = programContainer.duplicatableGroups;
    if (!duplicatableGroupMap) return;
    const map = new Map<string, number[]>();
    Object.keys(duplicatableGroupMap).forEach((key) => {
      const lastDashIndex = key.lastIndexOf("-");
      if (lastDashIndex === -1) {
        map.set(key, [0]);
      } else {
        const suffix = key.substring(lastDashIndex + 1);
        const count = Number(suffix);
        if (!Number.isInteger(count) || count <= 0) {
          map.set(key, [0]);
        } else {
          map.set(
            key,
            Array.from({ length: count }, (_, i) => i),
          );
        }
      }
    });
    DataUtils.resetDuplicateSections(programContainer, map);
  };

  static collectFormValue = (
    programContainer: ProgramContainer,
    siebelUpdatableOnly = "N",
    lang = "en",
    options?: { sendMetadataOnly?: boolean },
  ) => {
    // console.log(siebelUpdatableOnly);
    let obj = {};
    const shouldSanitize = programContainer?.ToSanitize === true;

    if (programContainer.GetAPI) {
      const regex = /\/\{(\w+)\}\/\{(\w+)\}/;
      const matches = programContainer.GetAPI.match(regex);
      if (matches) {
        for (let i = 1; i < matches.length; i++) {
          if (programContainer[`{${matches[i]}}`] == undefined)
            programContainer[`{${matches[i]}}`] = null;

          obj[matches[i]] = DataUtils.sanitizeCollectedValue(
            programContainer[`{${matches[i]}}`],
            undefined,
            shouldSanitize,
          );
        }
      }
    }

    const profile = programContainer.Profile;

    if (profile) {
      for (let formSection of programContainer.ListOfFormSections) {
        for (let subSection of formSection.ListOfFormSubSections) {
          for (let ele of subSection.ListOfFormSectionElements) {
            if (ele.Datatype == "HiddenField") {
              obj[ele.LoadBindName] = DataUtils.sanitizeCollectedValue(
                profile[ele.LoadBindName],
                ele.Datatype,
                shouldSanitize,
              );
            }
          }
        }
      }
    }

    for (let formSection of programContainer.ListOfFormSections) {
      for (let subSection of formSection.ListOfFormSubSections) {
        if (subSection.CanBeDuplicate) {
          // Section that could have duplicates
          let list: any[];
          if (obj[subSection.ENUTitle]) {
            list = obj[subSection.ENUTitle];
          } else {
            list = [];
            obj[subSection.ENUTitle] = list;
          }
          const sectionElement = {};
          for (let ele of subSection.ListOfFormSectionElements) {
            DataUtils.generateObject(
              programContainer,
              sectionElement,
              ele,
              siebelUpdatableOnly,
              lang,
              options,
            );
          }
          list.push(sectionElement);
        } else {
          for (let ele of subSection.ListOfFormSectionElements) {
            if (
              ele.Datatype == "HTML" ||
              ele.Datatype == "Label" ||
              ele.Datatype == "RichText" ||
              ele.Datatype == "TechError"
            ) {
              continue; // Skip elements that should not be collected
            }
            DataUtils.generateObject(
              programContainer,
              obj,
              ele,
              siebelUpdatableOnly,
              lang,
              options,
            );
          }
        }
      }
    }
    let jsonObject = DataUtils.unflattenObject(obj);

    return jsonObject;
  };

  private static sanitizeCollectedValue(
    value: any,
    datatype?: string,
    toSanitize: boolean = false,
  ) {
    if (value === null || value === undefined) return value;
    if (!toSanitize) return value;

    if (datatype !== "Text" && datatype !== "MultiText") {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) =>
        DataUtils.sanitizeCollectedValue(item, datatype, toSanitize),
      );
    }

    if (typeof value === "string") {
      return sanitizeString(value);
    }

    return value;
  }

  /**
   * Replaces the content inside <Data>...</Data> tags in a base64-encoded XML with "data" placeholder.
   * This is used when sendMetadataOnly is enabled to reduce payload size while preserving XML structure.
   */
  private static replaceXmlAttachmentData(base64Xml: string): string {
    try {
      // Decode base64 to get the XML string
      const xmlString = atob(base64Xml);
      // Replace content inside <Data>...</Data> tags with "data" placeholder
      const modifiedXml = xmlString.replace(
        /<Data>[\s\S]*?<\/Data>/g,
        "<Data>data</Data>",
      );
      // Re-encode back to base64
      return btoa(modifiedXml);
    } catch (e) {
      // If decoding/encoding fails, return the original
      console.error("Failed to replace XML attachment data:", e);
      return base64Xml;
    }
  }

  static generateObject(
    programContainer: ProgramContainer,
    obj: any,
    ele: any,
    siebelUpdatableOnly: string,
    lang: string,
    options?: { sendMetadataOnly?: boolean },
  ) {
    const collect = ele.input?.submitCollect ?? true;
    const shouldSanitize = programContainer?.ToSanitize === true;
    if (
      ele.Datatype == "Label" ||
      ele.Datatype == "Accordions" ||
      ele.Datatype == "HTML" ||
      ele.LoadBindName == "" ||
      ele.LoadBindName == null ||
      ele.Datatype == "Button" ||
      ele.Datatype == "DataTable" ||
      ele.Datatype == "PublicSecureButton" ||
      ele.Datatype == "Feedback" ||
      ele.Datatype == "SuccessAlert" ||
      ele.Datatype == "WarningAlert" ||
      ele.LoadBindName == undefined ||
      ele.Datatype == "CustomErrorAlerts" ||
      !collect ||
      ele?.excludedFromPayload
    ) {
      return;
    }
    if (siebelUpdatableOnly == "Y") {
      // console.log(ele);
      if (ele.UpdateField != null) {
        if (ele.Value == null) {
          if (ele.Datatype == "Checkbox") {
            ele.Value = "N";
          } else {
            ele.Value = "";
          }
        }
        obj[ele.UpdateField] = DataUtils.sanitizeCollectedValue(
          ele.Value,
          ele.Datatype,
          shouldSanitize,
        );
      }
    } else {
      if (ele.Datatype == "Row") {
        if (ele.subSectionList != null) {
          for (let eleSub of ele.subSectionList) {
            for (let eleSubSub of eleSub.ListOfFormSectionElements) {
              DataUtils.generateObject(
                programContainer,
                obj,
                eleSubSub,
                siebelUpdatableOnly,
                lang,
                options,
              );
            }
          }
        }
      } else if (ele.Datatype == "CMAQuestion") {
        // Handle CMA Question datatype
        obj[ele.LoadBindName] = DataUtils.sanitizeCollectedValue(
          ele.Value,
          ele.Datatype,
          shouldSanitize,
        );
        // Also save the additional comment if it exists
        if (
          ele.CMAQuestionComment !== undefined &&
          ele.CMAQuestionComment !== null
        ) {
          obj[ele.LoadBindName + "_additonal_comment"] =
            DataUtils.sanitizeCollectedValue(
              ele.CMAQuestionComment,
              ele.Datatype,
              shouldSanitize,
            );
        }
      } else if (
        ele.Datatype == "Checkbox" &&
        "Y" !== programContainer.CheckboxValueYN
      ) {
        obj[ele.LoadBindName] = ele.Value == "Y";
      } else if (ele.Datatype == "Address") {
        if (ele.returnAdrressObject) {
          obj[ele.LoadBindName] = ele.Address;
        } else {
          const addressStr = getAddress(
            ele.Address || programContainer.Profile?.address,
            false,
            lang,
          );
          const addressValue =
            addressStr && addressStr.trim() !== "" ? addressStr : ele.Value;
          obj[ele.LoadBindName] = DataUtils.sanitizeCollectedValue(
            addressValue,
            ele.Datatype,
            shouldSanitize,
          );
          if (ele.Address) {
            ele.Address.manualAddressChecked = ele.manualAddressChecked;
            obj[ele.LoadBindName + "_StringObject"] =
              programContainer?.ToSanitize
                ? sanitizeString(JSON.stringify(ele.Address))
                : JSON.stringify(ele.Address);
          }
        }
      } else if (ele.Datatype == "GoogleLookUpAddress") {
        if (ele.Value != null && ele["streetName"] != null) {
          obj[ele["unitNumber"]] = DataUtils.sanitizeCollectedValue(
            ele.Value != null ? ele?.Value["addressLine2"] : null,
            ele.Datatype,
            shouldSanitize,
          );
          const streetNumberPattern = /^\d+/;
          const match1 = ele.Value["addressLine1"]?.match(streetNumberPattern);
          if (match1) {
            obj[ele["streetNumber"]] = DataUtils.sanitizeCollectedValue(
              match1[0],
              ele.Datatype,
              shouldSanitize,
            );
          }
          const regex2 = /\d+\s+(.+)/;
          const match2 = ele.Value["addressLine1"]?.match(regex2);
          if (match2) {
            obj[ele["streetName"]] = DataUtils.sanitizeCollectedValue(
              match2[1],
              ele.Datatype,
              shouldSanitize,
            );
          }
          const regex3 = /(\bNorth\b|\bSouth\b|\bEast\b|\bWest\b)/;
          const match3 = ele.Value["addressLine1"]?.match(regex3);
          if (match3) {
            obj[ele["streetDirection"]] = DataUtils.sanitizeCollectedValue(
              match3[1],
              ele.Datatype,
              shouldSanitize,
            );
          }
          const regex4 =
            /\b(Street|Avenue|Boulevard|Drive|Road|Lane|Way|Circle|Parkway)\b/i;
          const match4 = ele.Value["addressLine1"]?.match(regex4);
          if (match4) {
            obj[ele["streetType"]] = DataUtils.sanitizeCollectedValue(
              match4[1],
              ele.Datatype,
              shouldSanitize,
            );
          }

          obj[ele["city"]] = DataUtils.sanitizeCollectedValue(
            ele.Value["city"],
            ele.Datatype,
            shouldSanitize,
          );
          obj[ele["province"]] = DataUtils.sanitizeCollectedValue(
            ele.Value["province"],
            ele.Datatype,
            shouldSanitize,
          );
          obj[ele["postalCode"]] = DataUtils.sanitizeCollectedValue(
            ele.Value["postalCode"],
            ele.Datatype,
            shouldSanitize,
          );
        }
      } else if (ele.Datatype == "File" && ele.Value) {
        obj[ele.LoadBindName] = ele.Value.map((file: UPloadFileItemProps) => {
          const fileObj: any = {
            path: file.file == null ? file.path : file.file.name,
            description: file.description ?? "",
            size: file.file == null ? file.size : file.file.size,
          };
          if (options?.sendMetadataOnly && file.attachment) {
            // Replace <Data>...</Data> content with "data" placeholder in the XML
            fileObj.attachment = DataUtils.replaceXmlAttachmentData(
              file.attachment,
            );
          } else if (!ele.FileUpload.multiPart && file.attachment) {
            fileObj.attachment = file.attachment;
          } else if (ele.FileUpload.multiPart && file.fileContent) {
            fileObj.attachment = file.fileContent;
          }
          return fileObj;
        });
        obj.multiPart = ele.FileUpload.multiPart;
      } else {
        const sanitizedValue = DataUtils.sanitizeCollectedValue(
          ele.Value,
          ele.Datatype,
          shouldSanitize,
        );
        if (
          programContainer?.CategoryId?.toLowerCase() === "glp" ||
          programContainer?.CategoryId?.toLowerCase() === "plp"
        ) {
          obj[ele.LoadBindName] =
            DataUtils.filterOutRestrictedChars(sanitizedValue);
        } else {
          obj[ele.LoadBindName] = sanitizedValue;
        }
      }
    }
  }

  static filterOutRestrictedChars(value: string): any {
    const restrictedCharsPattern = /[\"\[\]{}]/g;

    if (!value) {
      return value;
    }

    if (typeof value === "string") {
      return value.replace(restrictedCharsPattern, "");
    } else if (typeof value === "object" || Array.isArray(value)) {
      return DataUtils.filterCharactersInObject(value, restrictedCharsPattern);
    } else {
      return value;
    }
  }

  static filterCharactersInObject(obj: any, charactersToFilterPattern: RegExp) {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      if (typeof value === "string") {
        acc[key] = value.replace(charactersToFilterPattern, "");
      } else if (
        value !== null &&
        (typeof value === "object" || Array.isArray(value))
      ) {
        // Recursively handle nested objects
        acc[key] = DataUtils.filterCharactersInObject(
          value,
          charactersToFilterPattern,
        );
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  static flattenObject(obj: any, prefix = ""): FlattenedObject {
    const result: FlattenedObject = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          result[newKey] = value;
          const flattenedSubObject = DataUtils.flattenObject(value, newKey);
          Object.assign(result, flattenedSubObject);
        } else {
          result[newKey] = value;
        }
      }
    }

    return result;
  }

  private static unflattenObject(obj: any): FlattenedObject {
    const result: FlattenedObject = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const keys = key.split(".");
        let currentObj = result;
        for (let i = 0; i < keys.length - 1; i++) {
          const nestedKey = keys[i];
          if (
            !currentObj.hasOwnProperty(nestedKey) ||
            typeof currentObj[nestedKey] !== "object" ||
            currentObj[nestedKey] === null ||
            Array.isArray(currentObj[nestedKey])
          ) {
            currentObj[nestedKey] = {};
          }

          currentObj = currentObj[nestedKey];
        }

        const lastKey = keys[keys.length - 1];
        currentObj[lastKey] = value;
      }
    }

    return result;
  }

  static compareArraysOfObjects(arr1, arr2) {
    if ((arr1 == null && arr2 != null) || (arr2 == null && arr1 != null))
      return false;
    if (arr1 == null && arr2 == null) return true;

    if (
      Array.isArray(arr1) &&
      Array.isArray(arr2) &&
      arr1.length !== arr2.length
    ) {
      return false; // Different lengths, not equal
    }

    for (let i = 0; i < arr1.length; i++) {
      const obj1 = arr1[i];
      const obj2 = arr2.find((item) => this.areObjectsEqual(item, obj1));

      if (!obj2) {
        return false; // Object not found in arr2, not equal
      }
    }

    return true; // All objects found, arrays are equal
  }

  static areObjectsEqual(obj1, obj2) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false; // Different number of properties, not equal
    }

    for (let key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false; // Property values are not equal, not equal
      }
    }

    return true; // All properties are equal
  }

  static findControl = (field: FormSectionElements, targetFieldName) => {
    let returnedEle;
    const programContainer = getRootComponent(field);
    outerLoop: for (let formSection of programContainer.ListOfFormSections) {
      for (let subSection of formSection.ListOfFormSubSections) {
        for (let ele of subSection.ListOfFormSectionElements) {
          if (ele.LoadBindName == field.LoadBindName) {
            returnedEle = ele;
            break outerLoop;
          }
        }
      }
    }
    return returnedEle;
  };

  static clearFormValue(programContainer: ProgramContainer) {
    delete programContainer.CMAOptionList;
    delete programContainer.CMATemplateList;
    delete programContainer.CategoryNameList;
    delete programContainer.ClusterOptions;
    delete programContainer.FunctionNameList;
    delete programContainer.MinistryOptions;
    delete programContainer.OrganizationList;
    delete programContainer.SectorOptions;
    delete programContainer.SubCategoryNameList;

    for (let section of programContainer.ListOfFormSections) {
      if (!section.IsConfirmationPage) section.Visible = true;

      if (programContainer.Profile) {
        programContainer.Profile.address = {};
      }

      for (let i = section.ListOfFormSubSections.length - 1; i >= 0; i--) {
        if (section.ListOfFormSubSections[i].IsDuplicate)
          section.ListOfFormSubSections.splice(i, 1);
      }
      section.ListOfFormSubSections.forEach((subSection) => {
        subSection.ReadOnlyElements = false;
        subSection.ListOfFormSectionElements.forEach((ele) => {
          this.clearELementValue(ele);
        });
      });
    }

    return programContainer;
  }

  static clearELementValue(element: FormSectionElements) {
    if (element.Datatype === "Row") {
      if (element.subSectionList != null && element.subSectionList.length > 0) {
        element.subSectionList[0].ListOfFormSectionElements.map((ele) => {
          this.clearELementValue(ele);
        });
      }
    }
    if (
      element.Datatype != "Label" &&
      element.Datatype != "HTML" &&
      element.Datatype != "TechError"
    ) {
      element.Value = null;
      element.Valid = true;
      element.day = null;
      element.month = null;
      element.year = null;
      element.Visible = true;
      element.Touched = false;
    }

    if (
      element.Datatype == "SelectWithRichText" ||
      element.Datatype == "RadioButtonWithRichText"
    ) {
      element.SelectionCode = "";
    }
    if (element.Datatype == "CheckboxWithTextbox") {
      element.Checked = false;
    }
    if (element.Datatype == "Address") {
      element.Address = null;
    }
    if (element.Datatype === "File") {
      element.Value = null;
    }
  }

  static maskString = (
    inputString: string,
    startIndex: number,
    endIndex?: number,
    maskCharacter = "*",
  ) => {
    if (!endIndex) {
      endIndex = inputString?.length;
    }
    return inputString
      ? inputString.replace(
          new RegExp(`^(.{${startIndex}}).{${endIndex - startIndex}}`),
          (_, prefix) =>
            prefix + maskCharacter.repeat(endIndex || 0 - startIndex),
        )
      : null;
  };

  // static updateEntity = (programContainer: ProgramContainer) => {
  //   let entity: FormEntity = new FormEntity();
  //   entity.formId = programContainer.id;
  //   const formValue = DataUtils.collectFormValue(programContainer);
  //   console.log(formValue);
  //   entity.formValue = formValue;
  //   entity.id = formValue.id;
  //   entity.createdById=formValue.createdById;
  //   entity.createdDate=formValue.createdDate;
  //   entity.template=formValue.template;
  //   entity.status=formValue.status;
  //   programContainer.Entity=entity;
  // }

  static makeCompoenentReadonly = (programContainer) => {
    programContainer.ListOfFormSections.forEach((section) => {
      section.ListOfFormSubSections.forEach((sub) => {
        sub.ListOfFormSectionElements.forEach((ele) => {
          console.log(ele);
          ele.Editable = "N";
        });
      });
    });
  };

  static hideButtons = (programContainer) => {
    programContainer.ListOfFormSections.forEach((section) => {
      section.ListOfFormSubSections.forEach((sub) => {
        sub.ListOfFormSectionElements = sub.ListOfFormSectionElements.filter(
          (ele) => ele.Datatype !== "Button",
        );
      });
    });
  };

  static saveDraftToSession = async (
    programContainer: ProgramContainer,
  ): Promise<boolean> => {
    const data = collectDataForReload(programContainer);
    const lang = "EN";
    const stepperIndex =
      typeof programContainer.resumeStepperIndex === "number"
        ? programContainer.resumeStepperIndex
        : 0;
    const cachedData = {
      lang,
      data,
      stepperIndex,
      ...getDraftProgressMetadata(programContainer),
    };

    try {
      console.log("About to save draft to session-storage:", cachedData);
      sessionStorage.setItem(
        `draft_${programContainer.CategoryId}`,
        CircularJSON.stringify(cachedData),
      );
      if (programContainer.draftId) {
        const draftIdValue = programContainer.draftId.toString();
        sessionStorage.setItem(
          `draftId_${programContainer.CategoryId}`,
          draftIdValue,
        );
        const metadata: DraftMetadata = {
          draftId: draftIdValue,
          id: draftIdValue,
          formId: programContainer.CategoryId,
        };
        const profileEmail = DataUtils.resolveEmailCandidate(
          programContainer?.Profile?.email,
          programContainer?.Profile?.userId,
          (programContainer as any)?.UserId,
        );
        if (profileEmail) {
          metadata.userId = profileEmail;
        }
        DataUtils.persistDraftMetadata(programContainer.CategoryId, metadata);
      }
      return true;
    } catch (ex) {
      console.log("Failed to save draft to session-storage", ex);
      return false;
    }
  };

  static saveDraft = async (
    programContainer: ProgramContainer,
  ): Promise<boolean> => {
    const savedToSession = await DataUtils.saveDraftToSession(programContainer);

    const data = collectDataForReload(programContainer);
    const lang = "EN";
    const stepperIndex =
      typeof programContainer.resumeStepperIndex === "number"
        ? programContainer.resumeStepperIndex
        : 0;
    const cachedData = {
      lang,
      data,
      stepperIndex,
      ...getDraftProgressMetadata(programContainer),
    };
    const cxpAccessToken = getCxpAccessToken(programContainer.Profile?.token);
    if (!cxpAccessToken) {
      return savedToSession;
    }

    let fullUrl = getEnvVar("LAYOUT_API_URL") + "/data/admin/drafts";
    try {
      const draftMetadata = DataUtils.getDraftMetadataFromSession(
        programContainer.CategoryId,
      );
      if (!programContainer.draftId && draftMetadata?.draftId) {
        programContainer.draftId = draftMetadata.draftId;
      }
      const resolvedUserId = DataUtils.resolveEmailCandidate(
        draftMetadata?.userId,
        programContainer?.Profile?.email,
        programContainer?.Profile?.userId,
        (programContainer as any)?.UserId,
      );

      if (!programContainer.draftId && resolvedUserId) {
        const existingDraftMetadata = await DataUtils.findExistingDraftMetadata(
          programContainer.CategoryId,
          resolvedUserId,
          cxpAccessToken,
        );
        if (existingDraftMetadata?.draftId) {
          programContainer.draftId = existingDraftMetadata.draftId;
          DataUtils.persistDraftMetadata(programContainer.CategoryId, {
            ...existingDraftMetadata,
            formId: existingDraftMetadata.formId ?? programContainer.CategoryId,
          });
        }
      }

      let payload: any = {};
      payload.formValue = cachedData;
      payload.formId = programContainer.CategoryId;
      const queryDraftId = (() => {
        try {
          const params = new URLSearchParams(window.location.search);
          return params.get("draftId") || params.get("id");
        } catch {
          return null;
        }
      })();
      const submitApiDraftId = DataUtils.getDraftIdFromSubmitApiResponse();
      const resolvedDraftId =
        queryDraftId ??
        submitApiDraftId ??
        programContainer.draftId ??
        draftMetadata?.draftId;
      if (resolvedDraftId) {
        payload.draftId = resolvedDraftId;
        programContainer.draftId = resolvedDraftId;
      }
      const resolvedId = draftMetadata?.id ?? resolvedDraftId;
      if (resolvedId) {
        payload.id = resolvedId;
      }
      if (resolvedUserId) {
        payload.userId = resolvedUserId;
      }
      // const headers = {
      //   'Content-Type': 'application/json',
      //   'Authorization': `Bearer ${token}`,
      // }
      // { headers: headers }
      // if (payload.userId == null || payload.userId == undefined || payload.userId == "") {
      //   payload.userId = programContainer.Profile?.email;
      //   const headers = {
      //   'Content-Type': 'application/json',
      //   'X-User-Email': programContainer.Profile?.email,
      // }
      // }

      const sanitizedPayload = JSON.parse(
        JSON.stringify(payload, (key, value) =>
          typeof value === "string" ? sanitizeString(value) : value,
        ),
      );

      DataUtils.persistDraftMetadata(programContainer.CategoryId, {
        id: sanitizedPayload?.id,
        draftId: sanitizedPayload?.draftId,
        userId: sanitizedPayload?.userId,
        formId: sanitizedPayload?.formId ?? programContainer.CategoryId,
      });

      console.log("About to save draft to backend:", sanitizedPayload);
      const headers: { [key: string]: string } = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cxpAccessToken}`,
      };
      let response = await axios.post(fullUrl, sanitizedPayload, { headers });
      const responseDraftId =
        response?.data?.draftId ?? response?.data?.id ?? sanitizedPayload?.draftId;
      if (responseDraftId) {
        programContainer.draftId = responseDraftId.toString();
      }
      DataUtils.persistDraftMetadata(programContainer.CategoryId, {
        id: (responseDraftId ?? sanitizedPayload?.id)?.toString(),
        draftId: responseDraftId?.toString(),
        userId: sanitizedPayload?.userId,
        formId: sanitizedPayload?.formId ?? programContainer.CategoryId,
        saved: true,
      });
      return true;
    } catch (ex) {
      console.log(ex);
      return false;
    }
  };

  static saveDraftToDatabase = async (
    programContainer: ProgramContainer,
    api: string,
  ): Promise<boolean> => {
    const savedToSession = await DataUtils.saveDraftToSession(programContainer);
    const bpxAccessToken = SessionStorageService.getBPSToken();

    const data = collectDataForReload(programContainer);
    const lang = "EN";
    const stepperIndex =
      typeof programContainer.resumeStepperIndex === "number"
        ? programContainer.resumeStepperIndex
        : 0;
    const cachedData = {
      lang,
      data,
      stepperIndex,
      ...getDraftProgressMetadata(programContainer),
    };
    try {
      const draftMetadata = DataUtils.getDraftMetadataFromSession(
        programContainer.CategoryId,
      );
      if (!programContainer.draftId && draftMetadata?.draftId) {
        programContainer.draftId = draftMetadata.draftId;
      }
      const resolvedUserId = DataUtils.resolveEmailCandidate(
        draftMetadata?.userId,
        programContainer?.Profile?.email,
        programContainer?.Profile?.userId,
        (programContainer as any)?.UserId,
      );

      if (!programContainer.draftId && resolvedUserId && bpxAccessToken) {
        const existingDraftMetadata = await DataUtils.findExistingDraftMetadata(
          programContainer.CategoryId,
          resolvedUserId,
          bpxAccessToken,
        );
        if (existingDraftMetadata?.draftId) {
          programContainer.draftId = existingDraftMetadata.draftId;
          DataUtils.persistDraftMetadata(programContainer.CategoryId, {
            ...existingDraftMetadata,
            formId: existingDraftMetadata.formId ?? programContainer.CategoryId,
          });
        }
      }

      let payload: any = {};
      payload.formValue = cachedData;
      payload.formId = programContainer.CategoryId;
      payload.serviceId = getSelectedService()?.serviceId;
      const queryDraftId = (() => {
        try {
          const params = new URLSearchParams(window.location.search);
          return params.get("draftId") || params.get("id");
        } catch {
          return null;
        }
      })();
      const submitApiDraftId = DataUtils.getDraftIdFromSubmitApiResponse();
      const resolvedDraftId =
        queryDraftId ??
        submitApiDraftId ??
        programContainer.draftId ??
        draftMetadata?.draftId;
      if (resolvedDraftId) {
        payload.draftId = resolvedDraftId;
        programContainer.draftId = resolvedDraftId;
      }
      const resolvedId = draftMetadata?.id ?? resolvedDraftId;
      if (resolvedId) {
        payload.id = resolvedId;
      }
      if (resolvedUserId) {
        payload.userId = resolvedUserId;
      }

      const sanitizedPayload = JSON.parse(
        JSON.stringify(payload, (key, value) =>
          typeof value === "string" ? sanitizeString(value) : value,
        ),
      );

      DataUtils.persistDraftMetadata(programContainer.CategoryId, {
        id: sanitizedPayload?.id,
        draftId: sanitizedPayload?.draftId,
        userId: sanitizedPayload?.userId,
        formId: sanitizedPayload?.formId ?? programContainer.CategoryId,
      });

      console.log("About to save draft to backend:", sanitizedPayload);
      const headers: { [key: string]: string } = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bpxAccessToken}`,
      };
      let response = await axios.post(api, sanitizedPayload, { headers });
      return true;
    } catch (ex) {
      console.log(ex);
      return false;
    }
  };

  static clearDraft = (
    categoryId: string,
    email?: string,
    draftId?: string,
  ) => {
    const resolvedCategoryId = categoryId?.trim();
    const draftMetadata = resolvedCategoryId
      ? DataUtils.getDraftMetadataFromSession(resolvedCategoryId)
      : null;
    const resolvedDraftId =
      DataUtils.normalizeMetadataString(draftId) ??
      draftMetadata?.draftId ??
      draftMetadata?.id ??
      DataUtils.getDraftIdFromSubmitApiResponse();

    sessionStorage.removeItem(`draft_${categoryId}`);
    sessionStorage.removeItem(`draftId_${categoryId}`);
    sessionStorage.removeItem(`draftId_${categoryId.toLowerCase()}`);
    const metadataKey = DataUtils.buildDraftMetadataKey(categoryId);
    if (metadataKey) {
      sessionStorage.removeItem(metadataKey);
    }

    if (resolvedDraftId) {
      const fullUrl =
        getEnvVar("LAYOUT_API_URL") +
        "/data/admin/drafts/" +
        encodeURIComponent(resolvedDraftId);
      const cxpToken = getCxpAccessToken();
      const headers: Record<string, string> = cxpToken
        ? { Authorization: `Bearer ${cxpToken}` }
        : {};
      axios.delete(fullUrl, { headers }).catch((err) => {
        console.log("Failed to delete draft after submission", err);
      });
    }
  };
  static saveToServer = async (
    isSubmit: boolean,
    programContainer: ProgramContainer,
    isEng: boolean,
    htmlStr: any,
    growl,
    callback: (programContainer: ProgramContainer) => void,
  ): Promise<boolean> => {
    if (programContainer.UpdateAPI || programContainer.AuthRequired === "tk") {
      let payload = DataUtils.collectFormValue(programContainer);
      payload.IsComplete = isSubmit;
      if (!isSubmit) {
        payload.Attachment = [];
      }
      payload["UserId"] = programContainer["UserId"];

      if (!payload["FormId"]) payload["FormId"] = programContainer["FormId"];

      if (!payload["UserId"])
        payload["UserId"] = programContainer.Profile?.UserId;

      let response = null;
      let fullUrl =
        programContainer.APIBasePath + "/" + programContainer.UpdateAPI;
      // this.setState({ isBusy: true });
      try {
        if (programContainer.UseProxy == "Y") {
          let postObject = {
            url: fullUrl,
            httpMethod: "POST",
            payload: JSON.stringify(payload),
          };
          response = await axios.post(apiUrl() + "/transfer", postObject, {
            headers: {
              ApplicationId: programContainer.CategoryId,
              "Content-Type": "application/json",
            },
          });
          // this.setState({ isBusy: false });
          growl.show({
            severity: "success",
            summary: "Success Message",
            detail: "Data saved",
          });
        } else {
          if (programContainer.AuthRequired === "tk") {
            let fullPayload = {
              CategoryId: programContainer["CategoryId"],
              Name: isEng
                ? programContainer["Name"]
                : programContainer["NameFR"],
            };

            if (isSubmit) {
              fullPayload["payload"] = payload; //sanitizedPayload;

              const metaData = programContainer.MetaData || "";
              //fullPayload["payload"]=payload;
              fullPayload["action"] = "submit";

              if (metaData.includes("testit")) {
                htmlStr = [];
                htmlStr.push(
                  React.createElement("div", {
                    key: "1",
                    dangerouslySetInnerHTML: { __html: metaData },
                  }),
                );
              } else if (metaData.includes("teststring")) {
                htmlStr = metaData;
              }

              const pdfBase64 = await generateBase64PdfTemplate(
                programContainer,
                isEng ? "en" : "fr",
              );

              fullPayload["pdfData"] = pdfBase64
                ? pdfBase64
                : await generateHtmlForPdf(htmlStr, fullPayload["Name"]);

              //fullPayload["pdfData"] = await generatePdfFromHtml(htmlStr, fullPayload["Name"]);
            } else {
              payload = removeAttachmentSections(payload);
              fullPayload["payload"] = payload;
              fullPayload["action"] = "draft";
            }
            if (programContainer.itemId) {
              fullPayload["itemId"] = `${programContainer.itemId}`;
            }

            const { redirectUrl } = await saveToBxpFormApi(fullPayload, isEng);
            if (isSubmit) {
              DataUtils.clearDraft(
                programContainer.CategoryId,
                programContainer.Profile?.email,
                programContainer.draftId,
              );
              programContainer.draftId = undefined;
            }
            window.location.href = redirectUrl;

            /*programContainer.skipDirtyCheck = true;
                  callback(programContainer);
                  let ret_url=`${getEnvVar('BXP_UI_BASE_PATH')}/${isEng ? 'en/' : 'fr/'}integration/eform/confirmation?key=${key}`;
                  setTimeout(() => {
                    window.location.href= ret_url;
                  }, 100);*/

            return true;
          } else {
            if (programContainer.AuthRequired === "ad") {
              const token = await SessionStorageService.getAccessToken(
                TOKEN_TYPE.API,
              );
              const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              };
              payload.userId = SessionStorageService.getUserId();
              response = await axios.post(fullUrl, payload, {
                headers: headers,
              });
            } else {
              response = await axios.post(fullUrl, payload, {
                withCredentials: true,
              });
            }
            let data = response.data;

            if (
              isSubmit ||
              programContainer.AutoSavedEnabled ||
              programContainer.saveLaterEnabled
            ) {
              DataUtils.clearDraft(
                programContainer.CategoryId,
                programContainer.Profile?.email,
                programContainer.draftId,
              );
            }
            if (
              isSubmit == true &&
              "Y" == programContainer.HasSaveDraftButton
            ) {
              DataUtils.makeCompoenentReadonly(programContainer);
              // this.setState({ isComplete: true });
            }
            programContainer.Entity = data;
            DataUtils.assignFormValue(programContainer);
            // this.setState({ isBusy: false });
            growl.show({
              severity: "success",
              summary: "Success Message",
              detail: "Data saved",
            });
          }
        }
        return true;
      } catch (ex) {
        console.log(ex);
        // this.setState({ isBusy: false });
        if (growl) {
          growl.show({
            severity: "error",
            summary: "Error Message",
            detail: "Saving data failed",
          });
        }
        return false;
      }
    }
    return false;
  };
}

export const stashBxpPayload = async (
  fullPayload: any,
  isEng: boolean,
): Promise<{ key: string; dataString: string }> => {
  //alert(dataJson.pdfData)

  const formData = new FormData();
  const { cleanedPayload, attachments } = extractAttachments(
    fullPayload.payload,
  );
  console.log("cleanedPayload:", fullPayload);
  if (fullPayload.action === "submit") {
    if (!isBase64(fullPayload.pdfData)) {
      console.log("data from eform runtime: ", fullPayload);
      if (fullPayload.action === "submit") {
        const resp = await generatePdf(fullPayload.pdfData);
        const pdfData = await resp.json();
        console.log("pdfData runtime: ", pdfData);
        if (pdfData.error) throw new Error(pdfData.error);
        console.log("pdfData base64 length: ", pdfData.base64.length);
        console.log(
          "pdfData base64 sample: ",
          pdfData.base64.substring(0, 1000),
        );
        fullPayload.pdfData = pdfData.base64;
      }
    }
    const summaryPdf = base64ToFile(
      fullPayload.pdfData,
      "summaryPdf.pdf",
      "application/pdf",
    );
    if (attachments) {
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }
    formData.append("summaryPdf", summaryPdf);
  }

  fullPayload.payload = cleanedPayload;
  fullPayload.pdfData = undefined;
  const params = new URLSearchParams(window.location.search);
  const key = params.get("key");
  if (key) {
    const sessionData = sessionStorage.getItem("bxpFormData");
    if (sessionData && JSON.parse(sessionData)[key]) {
      const token = sessionData
        ? (JSON.parse(sessionData)["token"] ?? null)
        : null;
      fullPayload.token = token;
    }
  }
  const encoded = btoa(
    unescape(encodeURIComponent(sanitizeString(JSON.stringify(fullPayload)))),
  );
  const payloadBlob = new Blob([encoded], { type: "application/octet-stream" });
  formData.append("payloadFile", payloadBlob, "payload.bin");

  //formData.append("payload",sanitizeString(JSON.stringify(fullPayload)));
  try {
    const apiUrl = `${getEnvVar("BXP_UI_BASE_PATH")}/api/eform?type=saveForm&lang=${isEng ? "en" : "fr"}`;
    const res = await fetch(apiUrl, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!res.ok) {
      console.error("Failed to save form data to BXP Form API", {
        status: res?.status,
        statusText: res?.statusText,
      });
      throw new Error("saveForm failed with status " + res?.status);
    }
    const transData = await res.json();
    console.log("Received response from saveForm API:", transData);
    return { key: transData.key, dataString: transData.data };
  } catch (error) {
    console.error(
      "Error fetching calling saveForm:",
      error.response?.data || error.message,
    );
    throw error;
    //return undefined;
  }
};

const generatePdf = async (htmlString) => {
  const encoded = btoa(unescape(encodeURIComponent(htmlString)));
  const htmlBlob = new Blob([encoded], { type: "application/octet-stream" });
  const formData = new FormData();
  formData.append("file", htmlBlob, "document.bin");
  const resp = await fetch(`${getEnvVar("BXP_UI_BASE_PATH")}/api/eform/pdf`, {
    method: "POST",
    body: formData,
  });

  return resp;
};

export const saveToBxpFormApi = async (
  fullPayload: any,
  isEng: boolean,
): Promise<{ key: string; redirectUrl: string }> => {
  const { key, dataString } = await stashBxpPayload(fullPayload, isEng);
  if (dataString) {
    const current = new URL(window.location.href);
    const env = new URL(getEnvVar("BXP_UI_BASE_PATH"));
    const isSameOrigin = current.origin === env.origin;
    let keyTrans = null;
    const sessionData = sessionStorage.getItem("bxpFormData");
    const token = sessionData
      ? (JSON.parse(sessionData)["token"] ?? null)
      : null;
    const dateJson = JSON.parse(dataString);
    dateJson.token = token;
    if (!isSameOrigin) {
      const resp = await fetch(
        `${getEnvVar("BXP_UI_BASE_PATH")}/api/stash-payload?type=FORM`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dateJson),
        },
      );
      if (resp.status === 200) {
        const data: any = await resp.json();
        keyTrans = data.key;
        console.log("Fetch returned data:", data);
        return {
          key: keyTrans,
          redirectUrl: `${getEnvVar("BXP_UI_BASE_PATH")}/${
            isEng ? "en/" : "fr/"
          }integration/eform/confirmation?key=${encodeURIComponent(keyTrans)}`,
        };
      } else {
        throw new Error(
          "Failed to stash payload in BXP. Status: " + resp.status,
        );
      }
    } else {
      keyTrans = uuidv4();
      const dt: any = {};
      dt[keyTrans] = JSON.stringify(dateJson);
      sessionStorage.setItem("bxpTransData", JSON.stringify(dt));
      return {
        key: keyTrans,
        redirectUrl: `${getEnvVar("BXP_UI_BASE_PATH")}/${
          isEng ? "en/" : "fr/"
        }integration/eform/confirmation?key=${encodeURIComponent(keyTrans)}`,
      };
    }
  } else {
    return {
      key,
      redirectUrl: `${getEnvVar("BXP_UI_BASE_PATH")}/${
        isEng ? "en/" : "fr/"
      }integration/eform/confirmation?key=${encodeURIComponent(key)}`,
    };
  }
};

interface FlattenedObject {
  [key: string]: any;
}

export const NonInputFields = ["RichText", "Label", "HTML", "TechError"];

const shouldCountSectionInDraftProgress = (section: FormSections): boolean => {
  if (!section || section.Visible === false) {
    return false;
  }

  return !(
    section.IsFrontPage ||
    section.IsTerminationPage ||
    section.CustomizedReviewPage ||
    section.IsTechErrorPage ||
    section.IsConfirmationPage ||
    section.NotStep ||
    section.NotCountedInSteps
  );
};

const getDraftProgressMetadata = (programContainer: ProgramContainer) => {
  const countedSectionIndexes =
    programContainer?.ListOfFormSections?.reduce<number[]>(
      (indexes, section, index) => {
        if (shouldCountSectionInDraftProgress(section)) {
          indexes.push(index);
        }
        return indexes;
      },
      [],
    ) || [];

  const activeSectionIndex =
    typeof programContainer?.resumeStepperIndex === "number"
      ? programContainer.resumeStepperIndex
      : 0;

  return {
    totalSteps: countedSectionIndexes.length,
    completedSteps: countedSectionIndexes.filter(
      (index) => index < activeSectionIndex,
    ).length,
  };
};

const sanitizeDuplicateSubSection = (subSec: FormSubSections) => {
  if (!subSec) return subSec;
  const clean = (val: any) =>
    typeof val === "string" ? removeHtmlTags(val) : val;
  const cleanTitle = (val: any) => {
    if (typeof val !== "string") return val;
    const noHtml = removeHtmlTags(val);
    return noHtml.replace(/\s*\(optional\)/i, "").trim();
  };

  return {
    ...subSec,
    ENUTitle: cleanTitle(subSec.ENUTitle),
    FRATitle: cleanTitle(subSec.FRATitle),
    ListOfFormSectionElements:
      subSec.ListOfFormSectionElements?.map((field) => ({
        ...field,
        Value: clean(field.Value),
        ValueFRA: clean((field as any).ValueFRA),
        ENUTitle: cleanTitle(field.ENUTitle),
        FRATitle: cleanTitle(field.FRATitle),
        ErrorMessage: clean(field.ErrorMessage),
        ErrorMessageFRA: clean(field.ErrorMessageFRA),
        ErrorMessageRichText: clean(field.ErrorMessageRichText),
        ErrorMessageRichTextFRA: clean(field.ErrorMessageRichTextFRA),
      })) ?? [],
  };
};

export const collectDataForReload = (programContainer: ProgramContainer) => {
  const secVals = programContainer.ListOfFormSections.reduce(
    (secSum, sec, secIdx) => {
      //    if (sec.CustomizedReviewPage) return secSum;  // Skip saving data in customized-review page.
      const subSecVals = sec.ListOfFormSubSections.reduce(
        (subSum, subSec, subIdx) => {
          if (subSec.IsDuplicate) {
            // Deep-cloning the subsection.
            let subVal: FormSubSections = parseJson(
              stringifyJson(subSec, programContainer.CategoryId),
              programContainer.CategoryId,
            );
            if (programContainer?.ToSanitize === true) {
              subVal = sanitizeDuplicateSubSection(subVal) as FormSubSections;
            }
            subVal.parent = null;
            subVal.ListOfFormSectionElements.forEach(
              (field) => (field.parent = null),
            );
            subSum[`SubSection_${subIdx}_duplicate`] = subVal;
          } else {
            const fldVals = subSec.ListOfFormSectionElements.reduce(
              (fldsum, field, fldIdx) => {
                // Not persisting non-input, 'File' and fields with empty value.
                if (
                  !NonInputFields.includes(field.Datatype) &&
                  field.Datatype !== "File" &&
                  (field.Value || field.Address)
                ) {
                  const cleanedValue =
                    typeof field.Value === "string"
                      ? removeHtmlTags(field.Value)
                      : field.Value;
                  let val = { value: cleanedValue, address: field.Address };
                  if (field.SelectionCode) {
                    // For selected value of RadioButtonWithRichTextComponent.
                    val["selectionCode"] = field.SelectionCode;
                  }
                  val["loadBindName"] = field.LoadBindName || `Field_${fldIdx}`;
                  fldsum[`Field_${fldIdx}`] = val;
                }
                return fldsum;
              },
              {},
            );

            if (Object.keys(fldVals).length > 0) {
              subSum[`SubSection_${subIdx}`] = fldVals;
            }
          }

          return subSum;
        },
        {},
      );

      if (
        Object.keys(subSecVals).length > 0 ||
        shouldCountSectionInDraftProgress(sec)
      ) {
        secSum[`Section_${secIdx}`] = subSecVals;
      }
      return secSum;
    },
    {},
  );
  return secVals;
};

/**
 * Note: Whenever the mechanism of storing the flow-data changes,
 * catchFlowData, resumeFormValues, isStorageExists, cleanResumeData have been updated accordingly.
 */
export const catchFlowData = async (
  programContainer: ProgramContainer,
  lang = "EN",
) => {
  const data = collectDataForReload(programContainer);
  const cachedData = { lang, data };
  // sessionStorage.setItem(`${programContainer.CategoryId}_flowData`, CircularJSON.stringify(cachedData));
  await DBService.set(
    `${programContainer.CategoryId}_flowData`,
    CircularJSON.stringify(cachedData),
  );
};

export const resumeFormValues = async (programContainer: ProgramContainer) => {
  // const data = sessionStorage.getItem(`${programContainer.CategoryId}_flowData`);
  const data = await DBService.get(`${programContainer.CategoryId}_flowData`);
  let lang;

  if (data) {
    populateProgramContainer(data, programContainer);
  }

  return { programContainer, lang };
};

export const populateProgramContainer = (
  cachedData: any,
  programContainer: ProgramContainer,
  convertToJson = true,
) => {
  let jsonData = null;
  if (convertToJson) {
    jsonData = CircularJSON.parse(cachedData);
  } else {
    jsonData = cachedData;
  }
  const values = jsonData.data;
  const shouldUnsanitize = true;
  if (typeof jsonData.stepperIndex === "number") {
    programContainer.resumeStepperIndex = jsonData.stepperIndex;
  }

  programContainer.ListOfFormSections.forEach((sec, secIdx) => {
    if (!values[`Section_${secIdx}`]) return;
    sec.ListOfFormSubSections.forEach((subSec, subIdx) => {
      const subSecValues = values[`Section_${secIdx}`][`SubSection_${subIdx}`];
      if (!subSecValues) return;

      const byName: Record<string, any> = {};
      Object.values(subSecValues).forEach((savedVal: any) => {
        if (savedVal && savedVal.loadBindName) {
          byName[savedVal.loadBindName] = savedVal;
        }
      });

      subSec.ListOfFormSectionElements.forEach((fld, fldIdx) => {
        const val =
          (fld.LoadBindName && byName[fld.LoadBindName]) ||
          subSecValues[`Field_${fldIdx}`];

        if (!val) return;

        fld.Value = shouldUnsanitize ? unsanitizeDeep(val.value) : val.value;
        fld.Address = shouldUnsanitize
          ? unsanitizeDeep(val.address)
          : val.address;
        if (val.selectionCode) {
          fld.SelectionCode = shouldUnsanitize
            ? unsanitizeDeep(val.selectionCode)
            : val.selectionCode;
        }
      });
    });
  });

  // Reset all dirty flags after populating data so the form starts clean.
  SectionUtils.resetDirtyFlags(programContainer);

  const duplcateSubs = getDuplicatedSubSection(values, programContainer);
};

export const isStorageExists = async (categoryId: string): Promise<boolean> => {
  // return !!sessionStorage.getItem(`${categoryId}_flowData`);
  return !!(await DBService.get(`${categoryId}_flowData`));
};

const getDuplicatedSubSection = (
  data: any,
  programContainer: ProgramContainer,
) => {
  const duplcateSubs = Object.keys(data).forEach((key, index) => {
    const secIndex = key.split("_")?.[1];
    if (isNaN(parseInt(secIndex))) return;

    const dupSubs = Object.keys(data[key]).filter((subkey) =>
      subkey.includes("_duplicate"),
    );
    dupSubs.forEach((subKey) => {
      const subIndex = subKey.split("_")?.[1];
      if (isNaN(parseInt(subIndex))) return;

      const section = programContainer.ListOfFormSections[secIndex];
      const subsections = section.ListOfFormSubSections;
      const value = unsanitizeDeep(data[key][subKey]);
      if (value) {
        value.parent = section;
        value.ListOfFormSectionElements.forEach(
          (field) => (field.parent = value),
        );
        subsections.splice(subIndex, 0, { ...value, parent: section });
      }
    });
  });
};

export const cleanResumeData = async (appId: string) => {
  // sessionStorage.removeItem(`${appId}_flowData`)
  await DBService.delete(`${appId}_flowData`);
};

export const findPropertyByKey = (
  obj: any,
  propertyName: string,
  verifiedData = new Set(),
): any => {
  if (!obj || typeof obj !== "object") return null;

  if (verifiedData.has(obj)) return null;
  verifiedData.add(obj);

  if (obj.hasOwnProperty(propertyName)) {
    return obj[propertyName];
  }
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        const result = findPropertyByKey(value, propertyName, verifiedData);
        if (result !== null) {
          return result;
        }
      }
    }
  }

  return null;
};

export const syncAllLoadBindName = (
  obj: any,
  propertyName: string,
  propertyValue: any,
  verifiedData = new Set(),
): void => {
  if (!obj || typeof obj !== "object") return;

  if (verifiedData.has(obj)) return;
  verifiedData.add(obj);

  if (obj.hasOwnProperty("LoadBindName") && obj.LoadBindName === propertyName) {
    obj.Value = propertyValue;
  }

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        syncAllLoadBindName(value, propertyName, propertyValue, verifiedData);
      }
    }
  }
};

export const sanitizeEmail = (email: string): string => {
  if (!email) return "";

  return email
    .trim()
    .toLowerCase()
    .replace(/[<>'"&`\\/\x00-\x1f\x7f-\x9f]/g, (char) => {
      const entities: { [key: string]: string } = {
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#x27;",
        "&": "&amp;",
        "`": "&#x60;",
        "\\": "&#x5C;",
        "/": "&#x2F;",
      };
      return (
        entities[char] ||
        `&#x${char.charCodeAt(0).toString(16).padStart(2, "0")};`
      );
    })
    .replace(/javascript:/gi, "")
    .replace(/data:/gi, "")
    .replace(/vbscript:/gi, "")
    .replace(/on\w+=/gi, "");
};

export const sanitizeString = (input: string): string => {
  if (!input) return "";

  return (
    input
      .trim()
      .replace(/[\r\n\t]/g, " ")
      .replace(/\s+/g, " ")
      .replace(/[<>'"&`\\/\x00-\x1f\x7f-\x9f]/g, (char) => {
        const entities: { [key: string]: string } = {
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#x27;",
          "&": "&amp;",
          "`": "&#x60;",
          "\\": "&#x5C;",
          "/": "&#x2F;",
        };
        return (
          entities[char] ||
          `&#x${char.charCodeAt(0).toString(16).padStart(2, "0")};`
        );
      })
      .replace(/javascript:/gi, "")
      //.replace(/data:/gi, "")
      .replace(/vbscript:/gi, "")
      .replace(/on\w+=/gi, "")
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
  );
};

export const unsanitizeString = (input: string): string => {
  if (!input) return "";

  const decodeNumericEntity = (match: string, value: string, base: number) => {
    const codePoint = parseInt(value, base);
    if (Number.isNaN(codePoint)) return match;
    return String.fromCharCode(codePoint);
  };

  return input
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x60;/g, "`")
    .replace(/&amp;/g, "&")
    .replace(/&#x5C;/g, "\\")
    .replace(/&#x2F;/g, "/")
    .replace(/&#40;/g, "(")
    .replace(/&#41;/g, ")")
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) =>
      decodeNumericEntity(match, hex, 16),
    )
    .replace(/&#([0-9]+);/g, (match, dec) =>
      decodeNumericEntity(match, dec, 10),
    );
};

const unsanitizeDeep = (input: any, seen = new WeakMap<object, any>()): any => {
  if (typeof input === "string") {
    return unsanitizeString(input);
  }
  if (!input || typeof input !== "object") {
    return input;
  }

  const cached = seen.get(input);
  if (cached) {
    return cached;
  }

  if (Array.isArray(input)) {
    const result: any[] = [];
    seen.set(input, result);
    input.forEach((item, index) => {
      result[index] = unsanitizeDeep(item, seen);
    });
    return result;
  }

  const result: Record<string, any> = {};
  seen.set(input, result);
  Object.keys(input).forEach((key) => {
    result[key] = unsanitizeDeep(input[key], seen);
  });
  return result;
};

export const isNullOrEmpty = (value) => {
  return value === null || value === undefined || value === "";
};

export const removeAttachmentSections = (obj) => {
  if (obj !== null && typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(
          (item) =>
            item &&
            typeof item === "object" &&
            "path" in item &&
            "attachment" in item &&
            "size" in item,
        )
      ) {
        delete obj[key];
      }
    }
  }

  return obj;
};

export function isBase64(str: string): boolean {
  if (typeof str !== "string" || str.length === 0) {
    return false;
  }

  if (str.length % 4 !== 0) {
    return false;
  }

  // 2. Regular Expression Check (Character Set and Padding)
  // The regex validates:
  // a) Only Base64 characters (A-Z, a-z, 0-9, +, /).
  // b) Optional padding ('=') at the end.
  const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
  if (!base64Regex.test(str)) {
    return false;
  }

  // 3. Attempted Decode Check (The most definitive check)
  // A string can look like Base64 but still be invalid if the padding is wrong.
  try {
    // Use Node.js Buffer for server-side environments:
    if (typeof Buffer !== "undefined") {
      return Buffer.from(str, "base64").toString("latin1").length > 0;
    }

    // Use Browser's atob() for client-side environments:
    if (typeof atob !== "undefined") {
      // atob() will throw an error if the padding or sequence is invalid.
      const decoded = atob(str);
      // Optional: Check if the decoded string contains null or control characters
      // which might indicate a non-text (binary) Base64 string, but is usually valid.
      return decoded.length > 0;
    }

    // If neither Buffer nor atob is available (unlikely in modern JS/TS environments)
    return false;
  } catch (e) {
    // If decoding fails (e.g., atob throws an InvalidCharacterError), it's not valid Base64.
    return false;
  }
}

export function base64ToFile(
  base64: string,
  filename: string,
  mimeType: string,
): File {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], filename, { type: mimeType });
}

type AttachmentNode = {
  attachment: string;
  path: string;
  size: number;
};

export function extractAttachments(payload: Record<string, any>) {
  const attachments: File[] = [];
  const cleanedPayload: Record<string, any> = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (
      Array.isArray(value) &&
      value.every(
        (v) => v?.attachment && v?.path && typeof v?.size === "number",
      )
    ) {
      // Extract files
      value.forEach((item: AttachmentNode, index: number) => {
        const [meta, base64] = item.attachment.split(",");
        const mimeMatch = meta.match(/data:(.*?);base64/);
        if (!mimeMatch) {
          throw new Error("Invalid data URL");
        }
        const mimeType = mimeMatch[1];
        attachments.push(base64ToFile(base64, item.path, mimeType));
      });
    } else {
      cleanedPayload[key] = value;
    }
  });

  return { cleanedPayload, attachments };
}
